/**
 * Strict module-scoped command validation (mirrors api/command_validation.py).
 * Used client-side to block loading/API before unsupported prompts are sent.
 */
import { MODULE_TYPES } from '../constants/chatModuleConfig';

const TECH_DOC_COMMANDS = {
  'generate technical documentation': true,
  'produce architecture documentation': true,
  'generate functional documentation': true,
  'generate data model diagram': true,
  'generate database schema report': true,
  'generate reverse engineering summary': true,
};

const BRD_COMMAND = 'create business requirements documents for oracle forms(brd)';

/** Display text for the single supported BRD operation (shown in onboarding + composer prefill). */
export const BRD_GENERATION_COMMAND =
  'Create Business Requirements Documents for Oracle Forms(BRD)';

/**
 * Prefill text for the BRD composer after input files are attached.
 * Appends the Oracle Forms name when it can be inferred from attachment filenames.
 * @param {string[]} attachmentNames
 */
export function buildBrdPrefillCommand(attachmentNames = []) {
  const formName = extractBrdFormName('', attachmentNames);
  if (formName) {
    return `${BRD_GENERATION_COMMAND} for ${formName}`;
  }
  return BRD_GENERATION_COMMAND;
}

const TECH_DOC_LIST = [
  'Generate Technical Documentation',
  'Produce Architecture documentation',
  'Generate Functional Documentation',
  'Generate Data model diagram',
  'Generate Database Schema Report',
  'Generate Reverse Engineering Summary',
];

/** Leading bullets / symbols (first alternation). */
const LEADING_BULLET_RE =
  /^[\s\u2022\u2023\u25e6\u25aa\u25cf\u25cb\u2219\u00b7*\-–—>]+/u;
/** Leading ordered-list markers: 1. 2) 3: etc. */
const LEADING_NUMBER_RE = /^\d+(?:\.|\)|\]|:)\s*/u;

/**
 * Normalize command text for strict equality (not fuzzy matching).
 * Mirrors api/command_validation.normalize_command_text.
 */
export function normalizeCommandText(text) {
  let s = (text || '').trim();
  if (!s) return '';

  const lines = s.split(/\r?\n/).map((ln) => ln.trim()).filter(Boolean);
  s = lines[0] || s;

  s = s.replace(/[\u200b\uFEFF\u00ad]/g, '');
  s = s.toLowerCase();

  let prev;
  do {
    prev = s;
    s = s.replace(LEADING_BULLET_RE, '').trimStart();
    s = s.replace(LEADING_NUMBER_RE, '').trimStart();
  } while (s !== prev);

  s = s.replace(/\s+/g, ' ');
  s = s.replace(/\s*\(\s*/g, '(').replace(/\s*\)/g, ')');

  while (s.length && '.,!?;:'.includes(s[s.length - 1])) {
    s = s.slice(0, -1).trimEnd();
  }
  return s;
}

function isAllowedBrdCommand(normalized) {
  if (!normalized) return false;
  if (normalized === BRD_COMMAND) return true;
  return normalized.startsWith(`${BRD_COMMAND} `);
}

function isTechDocCommand(normalized) {
  return Boolean(normalized && TECH_DOC_COMMANDS[normalized]);
}

function unsupportedTechDocMessage() {
  return (
    '**Guidance** · Intelligent Documentation Suite\n\n' +
    'Supported operations for this module are:\n\n' +
    `${TECH_DOC_LIST.map((item) => `• ${item}`).join('\n')}\n\n` +
    'Please select one of the supported operations to continue.'
  );
}

function unsupportedBrdMessage() {
  return (
    '**Guidance** · BRD for Oracle Forms\n\n' +
    'Supported operations for this module are:\n\n' +
    '• Create Business Requirements Documents for Oracle Forms(BRD)\n\n' +
    'Please select the supported operation to continue.'
  );
}

/**
 * @param {{ moduleType: string, query: string, hasAttachments?: boolean, attachmentNames?: string[] }} params
 * @returns {{ allowed: boolean, status: string, assistantMessage: string }}
 */
export function validateGenerationCommand({
  moduleType,
  query,
  hasAttachments = false,
  attachmentNames = [],
}) {
  const normalized = normalizeCommandText(query);
  const isTechDoc = moduleType === MODULE_TYPES.TECH_DOC;

  if (!normalized) {
    return {
      allowed: false,
      status: 'unsupported',
      assistantMessage: isTechDoc ? unsupportedTechDocMessage() : unsupportedBrdMessage(),
    };
  }

  if (isTechDoc) {
    if (isAllowedBrdCommand(normalized)) {
      return {
        allowed: false,
        status: 'wrong_module',
        assistantMessage:
          '**Guidance** · Intelligent Documentation Suite\n\n' +
          'BRD generation is available in the **BRD for Oracle Forms** tab. ' +
          'Please navigate there to continue.',
      };
    }
    if (!isTechDocCommand(normalized)) {
      return {
        allowed: false,
        status: 'unsupported',
        assistantMessage: unsupportedTechDocMessage(),
      };
    }
    if (!hasAttachments) {
      return {
        allowed: false,
        status: 'missing_path',
        assistantMessage:
          '**Guidance** · Intelligent Documentation Suite\n\n' +
          'Upload a project **.zip**, then send the exact supported command ' +
          '(for example: **Generate Technical Documentation.**).',
      };
    }
    return { allowed: true, status: 'allowed', assistantMessage: '' };
  }

  if (isTechDocCommand(normalized)) {
    return {
      allowed: false,
      status: 'wrong_module',
      assistantMessage:
        '**Guidance** · BRD for Oracle Forms\n\n' +
        'Technical documentation generation is available in the ' +
        '**Intelligent Documentation Suite** tab. Please navigate there to continue.',
    };
  }
  if (!isAllowedBrdCommand(normalized)) {
    return {
      allowed: false,
      status: 'unsupported',
      assistantMessage: unsupportedBrdMessage(),
    };
  }
  const formName = extractBrdFormName(query, attachmentNames);
  if (!formName) {
    return {
      allowed: false,
      status: 'missing_form',
      assistantMessage:
        '**Guidance** · BRD for Oracle Forms\n\n' +
        'Include the Oracle Forms **form name** in your message ' +
        '(for example: `Create Business Requirements Documents for Oracle Forms(BRD) for EBAL006`).',
    };
  }
  return { allowed: true, status: 'allowed', assistantMessage: '' };
}

const BRD_SKIP_TOKENS = new Set([
  'BRD', 'FOR', 'THE', 'AND', 'CREATE', 'BUSINESS', 'ORACLE', 'FORMS',
  'REQUIREMENTS', 'DOCUMENTS',
]);

export function extractBrdFormName(query, attachmentNames = []) {
  const q = (query || '').trim();
  for (const name of attachmentNames) {
    const base = name.split(/[/\\]/).pop() || '';
    const stem = base.replace(/\.(xml|zip|fmb)$/i, '').trim();
    if (/^[A-Za-z][A-Za-z0-9_]{2,}$/.test(stem)) return stem.toUpperCase();
  }
  const patterns = [
    /\bform[:\s]+([A-Za-z][A-Za-z0-9_]{2,})\b/i,
    /\b([A-Za-z][A-Za-z0-9_]{2,})\s+form\b/i,
    /\bgenerate\s+brd\s+(?:for\s+)?([A-Za-z][A-Za-z0-9_]{2,})\b/i,
  ];
  for (const pat of patterns) {
    const m = q.match(pat);
    if (m) {
      const candidate = m[1].toUpperCase();
      if (!BRD_SKIP_TOKENS.has(candidate)) return candidate;
    }
  }
  const forMatches = [...q.matchAll(/\bfor\s+([A-Za-z][A-Za-z0-9_]{2,})\b/gi)];
  for (let i = forMatches.length - 1; i >= 0; i -= 1) {
    const candidate = forMatches[i][1].toUpperCase();
    if (!BRD_SKIP_TOKENS.has(candidate)) return candidate;
  }
  const tokens = q.match(/\b[A-Za-z][A-Za-z0-9_]{2,}\b/g) || [];
  for (const tok of tokens) {
    const up = tok.toUpperCase();
    if (!BRD_SKIP_TOKENS.has(up) && up.length >= 4) return up;
  }
  return null;
}
